import { Injectable } from '@angular/core';
import { ItemStatus } from '@app/enum/item-status.enum';
import { DirectusQuery } from '@app/interfaces/directus-query.interface';
import { DirectusSchema } from '@app/interfaces/directus-schema.interface';
import { Article, ContentRelationship, LinkRelationship } from '@app/models/article';
import { from, map, Observable } from 'rxjs';

import { DataService } from '../data/data.service';

@Injectable({
  providedIn: 'root'
})
export class ArticlesService extends DataService {
  protected override collection: keyof DirectusSchema = 'articles';
  protected override fields = [
    '*',
    'contents.articles_contents_id.*',
    'contents.articles_contents_id.groups.groups_id.*',
    'groups.groups_id.id,groups.groups_id.title,groups.groups_id.externalId',
    'links.links_articles_id.*',
    'links.links_articles_id.groups.groups_id.*'
  ];

  /*
   * getItems is used to override the extendsQuery for readItems in DataService
   */
  public override getItems<Page>(): Observable<Page[]> {
    const query = {
      filter: {
        status: {
          _in: [ItemStatus.Published, ItemStatus.Archived]
        }
      },
      sort: 'sort'
    };
    return super.getItems(this.extendQuery(query), true);
  }

  /*
   * Method is used to search for articles by title and content blocks
   */
  public search<T = Article>(query: string, limit = 10): Observable<T[]> {
    const searchQuery: DirectusQuery = {
      filter: {
        status: {
          _in: [ItemStatus.Published, ItemStatus.Archived]
        },
        _or: [
          { title: { _icontains: query } },
          { contents: { articles_contents_id: { title: { _icontains: query } } } },
          { contents: { articles_contents_id: { text: { _icontains: query } } } }
        ]
      },
      fields: ['id', 'slug', 'title', 'image', 'date_updated', 'date_created'],
      limit,
      sort: '-date_updated'
    };
    return from(this.directusService.readItems<T>(this.collection, this.extendQuery(searchQuery)));
  }

  /*
   * Method is used to get an article by id
   */
  public override getItem<Page>(id: string, force?: boolean, isPreview?: boolean): Observable<Page | null> {
    return super.getItem<Page>(id, force, isPreview).pipe(
      map((article: Page | null) => {
        if (!article) return article;
        // TODO - find a better way to query in the first place, see getDeepFilter
        // workaround because contents are returned like { contents_id: null }
        const articleWithContents = article as unknown as { contents: ContentRelationship[] };
        articleWithContents.contents = articleWithContents.contents.filter(
          (content: ContentRelationship) => content.articles_contents_id !== null
        );
        const articleWithLinks = article as unknown as { links: LinkRelationship[] | null };
        articleWithLinks.links =
          articleWithLinks.links !== null
            ? articleWithLinks.links.filter(
                (link: LinkRelationship) => (link as { links_id?: unknown }).links_id !== null
              )
            : [];
        return article;
      })
    );
  }

  /*
   * Method is used to override the default query for readItems, depending on m2m structure for groups
   */
  protected override extendQuery(query: DirectusQuery, isPreview?: boolean): DirectusQuery {
    // set fields from current service
    query.fields = this.fields;
    // set groups from auth service, only if extendQueryWithGroups is true
    const groups = this.authService.getPermissionGroups();
    if (groups.length > 0 && !isPreview) {
      if (!query.filter) {
        query.filter = {};
      }
      const groupsOr = [
        {
          groups: {
            groups_id: {
              externalId: {
                _in: groups
              }
            }
          }
        },
        {
          groups: {
            groups_id: {
              _empty: true
            }
          }
        }
      ];
      if (query.filter['_or']) {
        const existingOr = query.filter['_or'];
        delete query.filter['_or'];
        query.filter['_and'] = [{ _or: existingOr }, { _or: groupsOr }];
      } else {
        query.filter['_or'] = groupsOr;
      }
      query.deep = {
        contents: {
          articles_contents_id: this.getDeepFilter(groups)
        },
        links: {
          links_id: this.getDeepFilter(groups)
        }
      };
    }
    return query;
  }

  private getDeepFilter(groups: string[]): Record<string, unknown> {
    return {
      _filter: {
        status: {
          _in: [ItemStatus.Published]
        },
        groups: {
          groups_id: {
            externalId: {
              _in: groups
            }
          }
        }
      }
    };
  }
}
